import { useEffect, useState, useCallback } from 'react';

/**
 * Custom hook that manages drawer visibility based on scroll position
 * @param {string} containerId - The data-testid of the container to attach scroll listener to
 * @param {boolean} isMobile - Boolean indicating if the device is mobile
 * @param {number} threshold - Distance from bottom (in pixels) to trigger drawer visibility
 * @returns {[boolean, function]} [showDrawer, setShowDrawer] - Drawer state and setter function
 */
export const useScrollDrawer = (containerId, isMobile, threshold = 100) => {
  const [showDrawer, setShowDrawer] = useState(false);

  const handleScroll = useCallback(
    e => {
      if (!isMobile) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        setShowDrawer(true);
      } else {
        setShowDrawer(false);
      }
    },
    [isMobile, threshold],
  );

  useEffect(() => {
    const scrollContainer = document.querySelector(`[data-testid="${containerId}"]`);

    if (isMobile && scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isMobile, containerId, handleScroll]);

  return [showDrawer, setShowDrawer];
};

export default useScrollDrawer;
